import React, { useMemo, useState } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useSelector, useDispatch } from 'react-redux';

// Actions
import {
  fetchContacts,
  updateContactTags,
  fetchContactLabels,
} from '~/redux/actions/contacts-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';

//Utils
import { MESSAGES } from '~/utils/message';

// Components
import CreateTagModal from '~/components/Settings/Tags/CreateTag/index.jsx';
import MultiSelect from '~/components/General/MultiSelectNew';
import LimitReach from '~/components/General/LimitModal';
import OlcFormModal from '~/components/General/OlcFormModal';

//MUI Component
import Typography from '@mui/material/Typography';
import { Box, CircularProgress } from '@mui/material';

// Styles
import './styles.scss';

const TagsUpdate = (props) => {
  const {
    handleClose,
    open,
    header,
    selectedRows,
    setSelectedRows,
    type,
    selectedTags,
    setSelectedTags,
    search,
    applyFilters,
    filters,
    pagination,
  } = props;

  const [loading, setLoading] = useState(false);
  const [createTagModal, setCreateTagModal] = useState(false);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [validationError, setValidationError] = useState('');
  const dispatch = useDispatch();

  const tags = useSelector((state) => state.contactsReducers.labels);

  const memoizedOptions = useMemo(() => {
    const baseOptions = tags
      .filter((tag) => tag.id !== -1)
      .map((tag) => ({ id: tag.id, title: tag.title }));

    if (type === 'update') {
      baseOptions.unshift({
        id: '-1',
        title: 'Create New Tag',
        custom: true,
        func: () => setCreateTagModal(true),
      });
    }

    return baseOptions;
  }, [tags, type]);

  const handleTagsSelection = (selectedTag) => {
    if (Array.isArray(selectedTag)) {
      if (selectedTag.includes(-1)) {
        setSelectedTags(selectedTag.filter((e) => e === -1));
      } else {
        setSelectedTags(selectedTag);
      }
      if (selectedTag.length > 0) {
        setValidationError('');
      }
    }
  };

  const getContacts = () => {
    let payload = {
      pagination,
      search: search.length ? search : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
    };
    dispatch(fetchContacts(payload));
  };

  const closeModal = () => {
    setValidationError('');
    handleClose();
  };

  const handleUpdateTags = async (event) => {
    event?.preventDefault();

    if (selectedTags.length === 0) {
      setValidationError(MESSAGES.CONTACTS.TAGS_UPDATE_ERROR);
      return;
    }

    setValidationError('');
    setLoading(true);

    const selectedContacts = selectedRows.map((row) => Number(row)).join(',');
    const selectedTagIds = selectedTags
      .map((tag) => (typeof tag === 'object' ? tag.id : tag))
      .join(',');
    const payload = {
      contactIds: selectedContacts,
      tagIds: selectedTagIds,
      action: type === 'update' ? 'add' : 'remove',
      search: search.length ? search : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
    };
    const updateTags = await dispatch(updateContactTags(payload));
    if (updateTags.status === 200) {
      getContacts();
      dispatch(success(MESSAGES.CONTACTS.TAGS_UPDATE_SUCCESS_MESSAGE));
      setSelectedRows([]);
      setSelectedTags([]);
      closeModal();
      setLoading(false);
    } else {
      dispatch(failure(updateTags?.data?.message && MESSAGES.CONTACTS.TAGS_UPDATE_ERROR));
      setLoading(false);
    }
  };

  const actionLabel =
    type === 'update'
      ? MESSAGES.CONTACTS.DELETE.ADD_BUTTON
      : MESSAGES.CONTACTS.DELETE.REMOVE_BUTTON;

  return (
    <>
      <OlcFormModal
        onClose={closeModal}
        open={open}
        title={header}
        className="tagsUpdateModalWrapper"
        contentClassName="tagsUpdateModalContent"
        footer={
          <>
            <OlcButton variant="secondary" type="button" onClick={closeModal}>
              {MESSAGES.CONTACTS.TAGS_UPDATE_BTN_TEXT}
            </OlcButton>
            <OlcButton
              variant="primary"
              type="submit"
              form="tags-update-form"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                actionLabel
              )}
            </OlcButton>
          </>
        }
      >
        <Box
          id="tags-update-form"
          component="form"
          className="tagsUpdateForm"
          onSubmit={handleUpdateTags}
          noValidate
        >
          <Box className={`tagsUpdateField ${validationError ? 'hasError' : ''}`}>
            <MultiSelect
              component={'tag'}
              options={memoizedOptions}
              selectedValue={selectedTags.map((e) => (typeof e === 'object' ? e.id : e))}
              setSelectedValue={handleTagsSelection}
              placeHolderText={MESSAGES.CONTACTS.ADVANCE_FILTERS.TAG_SEARCH_PLACEHOLDER_NEW}
              noOptionText={MESSAGES.CONTACTS.ADVANCE_FILTERS.NO_SEARCH_TAG}
              enabledSearch={true}
              searchAutocomplete={true}
              setCreateTagModal={setCreateTagModal}
              type={type}
              customIcon={true}
            />
            {validationError ? (
              <Typography className="errorMessage" role="alert">
                {validationError}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </OlcFormModal>
      <CreateTagModal
        openModal={createTagModal}
        handleCloseModal={() => {
          setCreateTagModal(false);
          // Refresh contact labels to include newly created tags
          dispatch(fetchContactLabels());
        }}
        setSelectedTags={(newTag) => {
          setSelectedTags((prevTags) => [
            ...prevTags,
            ...newTag.map((tag) => ({ id: tag.id, title: tag.title })),
          ]);
        }}
        tagsClass={true}
        onLimitReached={(message) => {
          setCreateTagModal(false);
          setLimitMessage(message);
          setLimitModal(true);
        }}
      />
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="tags"
      />
    </>
  );
};

export default TagsUpdate;
